import {useCallback, useEffect, useMemo, useState} from 'react';
import {errorMessageKey} from '../../api';
import type {
  ChannelMenuItem,
  Modifier,
  Order,
  OrderLine,
  Satang,
} from '../../api/types';
import {useRouter} from '../../app/router';
import {ROUTES} from '../../app/routes';
import {
  lineTotalSatang,
  lineUnitSatang,
  useCart,
  type AddItemInput,
  type CartLine,
  type CartLineIssue,
} from '../../cart';
import {reasonMessageKey, useI18n, type MessageKey} from '../../i18n';
import {useMenu} from '../../menu';
import {useOrder} from '../../order';
import {useGuestSession} from '../../session';
import {
  ActionLink,
  BackLink,
  Button,
  Card,
  Money,
  QtyStepper,
  Screen,
  ScreenBody,
  ScreenFooter,
  Sheet,
  SplitRow,
  Stack,
  StatusPill,
  TopBar,
  cx,
} from '../../ui';
import {LanguageToggle} from '../Q02LanguageToggle';

/**
 * # Q5 · Cart — the review-before-committing screen.
 *
 * Route `/cart`. Entered from Q3's "ดูรายการอาหาร", from Q4 after an add, and by
 * a guest who reloaded the page mid-meal. Leaves to `/review` (Q7).
 *
 * ## The six decisions in this file that are not obvious
 *
 * **1 · There are no photographs here, ever — and that is the design, not a
 * fallback.** Most V1 merchants have no photography (03 §constraints), so the
 * no-photo case is the only case this screen renders. It is also the right cart:
 * a guest reviewing a decision they already made identifies a line by its name,
 * its modifiers and its price, and a column of thumbnails on a 4G connection
 * costs the perf budget for nothing. There is no `image_url` branch to fall out
 * of, so there is no state in which this screen looks unfinished. `Photo` would
 * return null here anyway; it is not imported, so the question cannot be
 * reopened by accident.
 *
 * **2 · Nothing on this screen is Pandan unless the server said so.** Every
 * figure goes through `Money`, and the only tone this file chooses by hand is
 * `neutral` — a menu-price estimate and a line total have no settlement meaning.
 * The one tone that could be green comes from `orderTotalTone(order)`, which
 * returns `verified` only for a server-settled order (D-021). A cart, by
 * construction, can never be one. The unpaid balance is said in a *word* —
 * `StatusPill tone="warning"` — rather than by tinting the figure amber, which
 * is the rule the money primitives encode.
 *
 * **3 · The big number in the footer is an estimate and says so, twice.** The
 * endpoint set gives a guest no way to have a server price an unsubmitted cart,
 * so the figure comes from `estimateSubtotalSatang` — menu prices, no VAT, no
 * service charge, no rounding. It is labelled `q5.estimatedTotal` with
 * `q5.estimateNote` underneath, and the notice above it (`q7.payLaterNote`) says
 * the other half out loud: the kitchen fires now and money happens at the
 * counter. The design package states the estimate in two registers on purpose
 * and that survives translation. The only figure presented as a *total* is
 * `Order.totals.total_satang` on a ticket the server already priced.
 *
 * **4 · An item that vanishes mid-session is loud, per line, and blocking.**
 * `cart.issues` is re-derived from the live projection on every render, so an
 * 86 arriving on the stream surfaces without anything having to remember to
 * re-check. A sold-out line keeps its row, gains an explicit notice naming the
 * dish and the reason, and disables the primary action until it is dealt with —
 * it is never silently dropped and never discovered at checkout (03 §Q5, §Q6).
 * A repriced line is *not* blocking: it shows the new price with an
 * acknowledge action, because the server charges the real number either way.
 *
 * **5 · Issues are suppressed while the menu is still loading.**
 * `findCartIssues` reports every line as `gone` when the projection is empty,
 * which is exactly what a cold load of `/cart` looks like for the first second.
 * Rendering that would tell a guest their whole order sold out. So the per-line
 * notices only appear once `menu.status === 'ready'`, and the primary action is
 * held with `common.loading` until then. If the menu *fails*, the action is
 * released instead: the server rejects unavailable lines anyway (Q7 shows
 * `q7.someItemsSoldOut`), and stranding a guest who cannot send is worse than a
 * recoverable rejection.
 *
 * **6 · "Empty" is two questions, and this screen has to answer both.** An empty
 * cart is not an empty table. A guest who sent ฿900 of food and opened `/cart`
 * to check on it has an empty *cart* — there is nothing left to edit or send —
 * and a very much non-empty *table*. The design prototype gates its empty
 * message on `cart.length === 0 && sentCart.length === 0`; the port
 * (`QROrder.tsx:312`) dropped the second half and this file inherited it, which
 * put "ยังไม่มีรายการอาหาร" directly above the ticket listing what the guest had
 * just ordered — the screen contradicting itself on one viewport. So there are
 * two flags. `empty` is cart-only and still decides everything that is genuinely
 * about the cart: the footer (nothing to send means no send bar — the
 * prototype's own `hasNewItems`), the line count under the title, and, through
 * the footer, whether `ScreenBody` has anything to pin a scroll against.
 * `nothingHere` is `empty` *and* no live order line, and it is the only thing
 * allowed to say there is nothing here. Between them sits the third state the
 * port could not express — cart empty, ticket present: no send bar, no empty
 * message, the ticket, and one red `q5.addMore` under it, because ordering the
 * next round is the only forward move left on the screen.
 *
 * ## Confirm-before-send, and why there is no bottom sheet on this screen
 *
 * The design package draws the cart's red button opening a confirm sheet, and
 * the README calls that non-negotiable: **placing an order must go through an
 * explicit confirm step, and the primary CTA must never be wired straight to the
 * send action.** That invariant holds here, and it holds without a sheet,
 * because this app already spends a whole screen on it.
 *
 * `q5.reviewOrder` navigates to `/review` — Q7, whose title is `q7.title`
 * ("confirm the order"), whose body copy is `q7.payLaterNote` (the same sentence
 * the design's sheet carries), and whose one solid red button is `q7.submit`.
 * Q7 *is* the sheet, rendered as a screen, and it is the only place on this
 * surface that calls `POST /v1/orders`. Adding `Sheet` here would put a third
 * gate between a guest and their food — cart → sheet → confirm screen → send —
 * and `src/ui/Sheet.tsx` argues the opposite in as many words: a second "are you
 * sure" makes the first one furniture. Collapsing the two into one sheet on this
 * screen is a flow change rather than a redesign; it would move the submit, the
 * idempotency key, the offline re-send and the server's `item_unavailable`
 * handling out of Q7, and it is escalated rather than done quietly.
 *
 * ## Deliberately absent
 *
 * * **No payment, no total-to-pay, no checkout.** This said "the checkout
 *   endpoints are PLACEHOLDER (TBD)" until 2026-08-12, and they are not any
 *   more — Q8 exists. It stays absent here anyway: a checkout is created against
 *   an **order**, priced by the server, and a cart is neither. There is nothing
 *   on this screen a payment could be for.
 * * **No edit-line affordance** (`q5.edit` is unused). Q4 takes an item id, not
 *   a cart line, so an "แก้ไข" link would add a second line rather than change
 *   the one the guest tapped. Quantity is editable in place; anything else waits
 *   for a Q4 that can open an existing line.
 * * **No venue order limit** (`q5.overVenueLimit` is unused). No field in the
 *   contract carries one and this screen will not invent a number.
 * * **No AI add-on suggestions.** V2, explicitly not V1 (03 §Q5).
 *
 * ## Type, colour and geometry
 *
 * Every string on this screen is at least `--text-body-size` (16px); the
 * design's four tiers of small text are re-expressed as `.pk-strong` /
 * `.pk-muted` / `.pk-hint`, because ~13px clips Thai tone marks and descenders.
 * Colour comes only from tokens — there is not one hex literal below, and the
 * warm hairline is `--pokpok-color-border-warm` rather than astryx's
 * `--color-border`, which is a cool grey and reads blue on Sticky Rice. The
 * shell, the buttons, the stepper, the cards and the pills are the shared
 * primitives in `src/ui`, so this screen owns layout and nothing else.
 */

function findModifier(item: ChannelMenuItem, modifierId: string): Modifier | undefined {
  for (const group of item.modifier_groups ?? []) {
    const found = group.modifiers.find((m) => m.modifier_id === modifierId);
    if (found) return found;
  }
  return undefined;
}

/**
 * Rebuild the `add()` input for a line that was just removed, so "เอากลับ"
 * restores exactly what was there.
 *
 * Returns null when the item — or any modifier it carried — is no longer in the
 * menu, which is the sold-out case. Undo is then not offered at all, because an
 * undo button that puts back something the kitchen has run out of is worse than
 * no undo button.
 */
function toAddInput(line: CartLine, item: ChannelMenuItem): AddItemInput | null {
  const modifiers: {modifier: Modifier; quantity: number}[] = [];
  for (const chosen of line.modifiers) {
    const modifier = findModifier(item, chosen.modifier_id);
    if (!modifier) return null;
    modifiers.push({modifier, quantity: chosen.quantity});
  }
  return {item, quantity: line.quantity, modifiers, note: line.note};
}

/**
 * The lines the kitchen actually has, which is not the same as `order.lines`.
 *
 * Voided lines are excluded because a voided line is food the guest no longer
 * has: counting one would keep a ticket — and the "you already ordered"
 * framing that goes with it — on screen after the last of it was cancelled,
 * and would silence the empty message for a table with nothing on it.
 *
 * One function, called once per render, because the answer decides two separate
 * things (whether the ticket renders, and whether the empty message may be
 * said) and those two must never be able to disagree.
 */
function liveOrderLines(order: Order | null): OrderLine[] {
  return order ? order.lines.filter((line) => !line.voided) : [];
}

interface Undoable {
  name: string;
  /** Null when the line cannot be put back. The undo action is then not rendered. */
  input: AddItemInput | null;
}

export default function Q05Cart() {
  const {t, ln, money} = useI18n();
  const cart = useCart();
  const menu = useMenu();
  const order = useOrder().order;
  const {status: sessionStatus} = useGuestSession();
  const {navigate} = useRouter();

  const [undoable, setUndoable] = useState<Undoable | null>(null);

  // The confirmation is transient by design: it must not become a permanent
  // strip a guest has to dismiss before they can reach the send button.
  useEffect(() => {
    if (!undoable) return;
    const timer = globalThis.setTimeout(() => setUndoable(null), 8000);
    return () => globalThis.clearTimeout(timer);
  }, [undoable]);

  // See note 5 in the header: an empty projection makes every line look gone.
  const showIssues = menu.status === 'ready';

  const issueByLine = useMemo(() => {
    const map = new Map<string, CartLineIssue>();
    if (showIssues) for (const issue of cart.issues) map.set(issue.line.line_id, issue);
    return map;
  }, [showIssues, cart.issues]);

  const unavailableCount = useMemo(
    () => (showIssues ? cart.issues.filter((issue) => issue.kind !== 'reprice').length : 0),
    [showIssues, cart.issues],
  );

  const remove = useCallback(
    (line: CartLine) => {
      const item = menu.itemsById.get(line.item_id);
      const restorable =
        item && menu.isAvailable(line.item_id) ? toAddInput(line, item) : null;
      cart.remove(line.line_id);
      // The name is snapshotted as `LocalizedText`, so the toast reads in the
      // language the guest is in, not the language the line was added in.
      setUndoable({name: ln(line.name_snapshot), input: restorable});
    },
    [cart, menu, ln],
  );

  const undo = useCallback(() => {
    if (undoable?.input) cart.add(undoable.input);
    setUndoable(null);
  }, [cart, undoable]);

  const sessionBroken = sessionStatus === 'expired' || sessionStatus === 'invalid';
  const menuPending = menu.status === 'idle' || menu.status === 'loading';
  const menuFailed = menu.status === 'error';

  const hold: {key: MessageKey; retry?: () => void} | null = sessionBroken
    ? {key: 'error.session'}
    : menuPending
      ? {key: 'common.loading'}
      : menuFailed
        ? {key: errorMessageKey(menu.error), retry: menu.reload}
        : null;

  // A menu that failed to load does not block the send: the server is the
  // authority on availability and Q7 handles its rejection honestly.
  const blocked = sessionBroken || menuPending || (showIssues && !cart.submittable);

  // See note 6 in the header. `empty` answers "is there anything to send?" and
  // owns the send bar; `nothingHere` answers "is there anything at all?" and is
  // the only flag permitted to say so out loud.
  const sentLines = useMemo(() => liveOrderLines(order), [order]);
  const empty = cart.lines.length === 0;
  const nothingHere = empty && sentLines.length === 0;

  /*
   * The confirm sheet — this surface's send step since 2026-08-14 (owner
   * directive), replacing the review screen at `/review`.
   *
   * `submit()` is `OrderProvider`'s, not this screen's, which is why the move
   * cost one call rather than a refactor: the client-minted order id, the
   * idempotency key reused across every retry, and the re-send on `online` all
   * stay where they were and are unaware of which screen asked. What moved is
   * only the question.
   *
   * On success the guest goes to `/review`, which now renders **only** its sent
   * / rejected / queued panels — the confirmation the package draws as
   * `success`. On failure the sheet closes and Q7 shows why, because a failure
   * needs more room than a sheet has and one of its cases (`item_unavailable`)
   * sends the guest back here with work to do.
   */
  const [confirming, setConfirming] = useState(false);
  const {submit, submitStatus} = useOrder();
  const submitting = submitStatus === 'submitting';

  const confirmSend = useCallback(async () => {
    await submit();
    setConfirming(false);
    navigate(ROUTES.review);
  }, [submit, navigate]);

  return (
    <Screen>
      <style dangerouslySetInnerHTML={{__html: STYLES}} />

      <TopBar
        leading={<BackLink to={ROUTES.menu} label={t('common.back')} />}
        // INTEGRATION · the one shared control (Q02), not a fifth spelling.
        trailing={<LanguageToggle variant="link" />}
      />

      {/* `scroll` only where there is a footer to pin — the cart's question,
          not the table's. The send bar belongs to the editable cart, so an
          empty cart has no footer even with a long ticket on screen, and that
          case wants the document scroll too: it collapses the browser's URL bar
          where an inner one does not — real vertical space on a small phone. */}
      <ScreenBody scroll={!empty}>
        {/* One 16px rhythm down the whole body. `ScreenBody` already owns the
            gutter and the vertical padding; a screen that adds its own on top is
            how thirteen screens end up with thirteen different top margins. */}
        <Stack gap="wide">
          <div>
            <h1 className="pk-h1">{t('q5.title')}</h1>
            {!empty && <p className="pk-muted">{t('common.items', {count: cart.count})}</p>}
          </div>

          {unavailableCount > 0 && (
            <Card tone="warning" padding="tight" role="status">
              <div className="q5-notice">
                <p className="pk-strong q5-notice-text">
                  <span aria-hidden="true">⚠ </span>
                  {t('error.itemUnavailable')}
                </p>
                {unavailableCount > 1 && (
                  <Button variant="destructive" onClick={cart.removeUnavailable}>
                    {t('q5.lineSoldOutAction')}
                  </Button>
                )}
              </div>
            </Card>
          )}

          {/* `nothingHere`, not `empty`: this sentence is a claim about the
              whole table, and it is false the moment the kitchen holds a line.
              The three states are mutually exclusive by construction — nothing
              at all, an editable cart, or a cart that has already gone. */}
          {nothingHere && (
            <section className="q5-empty">
              <p className="pk-title">{t('q5.empty')}</p>
              {/* The screen's one solid red, and the only one it can have here:
                  with nothing in the cart there is no forward action but the
                  menu. When there IS a cart, this becomes the quiet text button
                  in the footer and the red moves to the send bar. */}
              <ActionLink to={ROUTES.menu} variant="primary">
                {t('q5.emptyAction')}
              </ActionLink>
            </section>
          )}

          {!empty && (
            <section className="pk-stack" aria-labelledby="q5-yours">
              <h2 className="pk-section-title" id="q5-yours">
                {t('q5.yourItems')}
              </h2>
              <ul className="q5-list">
                {cart.lines.map((line) => (
                  <CartRow
                    key={line.line_id}
                    line={line}
                    issue={issueByLine.get(line.line_id) ?? null}
                    onRemove={remove}
                    onSetQuantity={cart.setQuantity}
                    onAcceptPrice={cart.acceptNewPrice}
                  />
                ))}
              </ul>
            </section>
          )}

          {order && sentLines.length > 0 && <Ticket lines={sentLines} />}

          {/* Cart empty, kitchen busy. The send bar went with the cart it
              belonged to, so this is the only forward action on the screen and
              it takes the red — one primary action, still exactly one.
              `q5.addMore` rather than `q5.emptyAction`: the guest is adding a
              round to a meal in progress, not starting from nothing. It sits
              under the ticket because that is the order it is read in — here is
              what you have, now order more. */}
          {empty && !nothingHere && (
            <ActionLink to={ROUTES.menu} variant="primary">
              {t('q5.addMore')}
            </ActionLink>
          )}

          {/*
            `common.attribution` is gone from this screen too (same directive).
            It sat immediately under the totals it was removed with, and on a
            screen whose whole content is the guest's own order, a line naming
            the software is the least useful thing on it. pokpokPOS still says
            its name on the screens that end a journey — the sent confirmation,
            the status screen and the receipt — which is where D-014 wanted it:
            present, subordinate, and never a lockup.
          */}
        </Stack>
      </ScreenBody>

      {!empty && (
        <ScreenFooter>
          {undoable && (
            <Card padding="tight" role="status">
              <div className="q5-notice">
                <p className="q5-notice-text">{t('q5.removed', {item: undoable.name})}</p>
                {undoable.input && (
                  <Button variant="text" onClick={undo}>
                    {t('q5.undo')}
                  </Button>
                )}
              </div>
            </Card>
          )}

          {/*
            The pay-later notice used to sit here, above the figure, so a guest
            reading the number read the terms with it. **Removed by owner
            directive, 2026-08-14.**

            It is not gone from the surface: `q7.payLaterNote` is still on Q7,
            one screen further along, where the guest is looking at the same
            total with a send button under it. The disclosure therefore still
            happens before anything is ordered — it happens once instead of
            twice, on the screen where the commitment is actually made.
          */}

          <div className="q5-estimate">
            <SplitRow>
              <span className="pk-strong">{t('q5.estimatedTotal')}</span>
              {/*
                * neutral, not pending and never verified: this is arithmetic on
                * menu prices, not a bill anybody has priced or owes.
                */}
              <Money satang={cart.estimatedSubtotalSatang} tone="neutral" size="title" />
            </SplitRow>
            {/* The design pulls this up against the figure with a negative
                margin. A tighter gap says the same thing without one. */}
            <p className="pk-hint">{t('q5.estimateNote')}</p>
          </div>

          {hold && (
            <p className="q5-hold" role="status">
              <span className="pk-muted">{t(hold.key)}</span>
              {hold.retry && (
                <Button variant="text" onClick={hold.retry}>
                  {t('common.retry')}
                </Button>
              )}
            </p>
          )}

          {/*
            The one solid red on this screen, and it still does not send: it
            opens the confirm sheet, which does. Owner directive 2026-08-14
            replaced the review *screen* with this sheet — the package's own
            shape — and the README's invariant is unchanged and still met: an
            order is placed only through an explicit confirm step, never by this
            button alone.
          */}
          <Button
            variant="primary"
            disabled={blocked}
            disabledReason={hold ? t(hold.key) : t('error.itemUnavailable')}
            onClick={() => setConfirming(true)}>
            {t('q5.reviewOrder')}
          </Button>
          <ActionLink to={ROUTES.menu} variant="text" block>
            {t('q5.addMore')}
          </ActionLink>
        </ScreenFooter>
      )}

      {/*
        Confirm before send, as the package draws it: a sheet over the cart the
        guest is looking at, rather than a screen that replaces it. The body
        repeats what they are agreeing to — how many items, roughly how much, and
        that money happens at the counter — because a sheet asking "are you sure"
        about something scrolled out of view behind it is a sheet that gets
        dismissed to go and check.

        Confirm first, cancel second, per `Sheet`'s own contract. Cancel is
        outlined and never Dried Chili: closing a sheet destroys nothing.
      */}
      <Sheet
        open={confirming}
        title={t('q5.confirmTitle')}
        onCancel={() => setConfirming(false)}>
        <p className="pk-muted">
          {t('q5.confirmBody', {
            count: String(cart.lines.length),
            total: money(cart.estimatedSubtotalSatang),
          })}
        </p>
        <Button variant="primary" disabled={submitting} onClick={confirmSend}>
          {submitting ? t('q7.submitting') : t('q5.confirmSend')}
        </Button>
        <Button variant="secondary" block onClick={() => setConfirming(false)}>
          {t('common.cancel')}
        </Button>
      </Sheet>
    </Screen>
  );
}

/** One editable line of the cart, plus whatever is wrong with it. */
function CartRow({
  line,
  issue,
  onRemove,
  onSetQuantity,
  onAcceptPrice,
}: {
  line: CartLine;
  issue: CartLineIssue | null;
  onRemove: (line: CartLine) => void;
  onSetQuantity: (lineId: string, quantity: number) => void;
  onAcceptPrice: (lineId: string, unitPriceSatang: Satang) => void;
}) {
  const {t, ln, money, signedMoney} = useI18n();
  const {availabilityOf} = useMenu();

  const name = ln(line.name_snapshot);
  const out = issue?.kind === 'sold_out' || issue?.kind === 'gone';
  const newPrice = issue?.kind === 'reprice' ? (issue.currentUnitPriceSatang ?? null) : null;

  const reason = availabilityOf(line.item_id)?.reason ?? null;
  const reasonKey = reasonMessageKey(reason) ?? undefined;

  return (
    <li className={cx('q5-line', out && 'q5-line--out')}>
      <Card padding="regular" as="div" className="pk-stack">
        <div className="q5-line-head">
          <div className="pk-stack q5-line-id">
            <p className="pk-strong">{name}</p>
            {line.modifiers.length > 0 && (
              <p className="pk-muted">
                {line.modifiers
                  .map((modifier) => {
                    const label = ln(modifier.name_snapshot);
                    // `signedMoney`, never `money` with a hand-written sign: the
                    // design prototype printed "฿-5" that way, and a true minus
                    // also keeps a column of deltas aligned.
                    const delta =
                      modifier.price_delta_satang !== 0
                        ? ` ${signedMoney(modifier.price_delta_satang)}`
                        : '';
                    const times = modifier.quantity > 1 ? ` ×${modifier.quantity}` : '';
                    return `${label}${times}${delta}`;
                  })
                  .join(' · ')}
              </p>
            )}
            {line.note ? (
              <p className="pk-muted">
                {t('q4.noteToKitchen')}: {line.note}
              </p>
            ) : null}
            {out && <StatusPill tone="warning">{t('common.soldOut')}</StatusPill>}
          </div>

          <div className="q5-line-price">
            <Money satang={lineTotalSatang(line)} tone="neutral" size="md" />
            {line.quantity > 1 && (
              <p className="pk-muted pk-money q5-line-unit">
                {money(lineUnitSatang(line))} × {line.quantity}
              </p>
            )}
          </div>
        </div>

        <div className="q5-controls">
          {/*
            `min={1}`, so `−` stops at one instead of deleting the line.
            The design drops the line at zero, which is a destructive action
            wearing a neutral control's clothes — and it sits on the same row as
            an explicit delete that already does exactly that, with a word on it.
            `QtyStepper` documents the same choice; the labelled button is the
            one that removes.

            `max` pins `+` shut on a sold-out line: adding more of something the
            kitchen has run out of cannot be sent.
          */}
          <QtyStepper
            value={line.quantity}
            min={1}
            max={out ? line.quantity : undefined}
            onChange={(next) => onSetQuantity(line.line_id, next)}
            decreaseLabel={`${t('q4.decrease')} · ${name}`}
            increaseLabel={`${t('q4.increase')} · ${name}`}
            valueLabel={`${t('common.quantity')} · ${name}`}
          />

          {/* Destructive: outlined Dried Chili, never filled, and never a bare
              glyph — the word is what a screen reader and a hurried guest read
              (foundations §5). */}
          <Button variant="destructive" onClick={() => onRemove(line)}>
            <span aria-hidden="true">✕</span> {t('q5.remove')}
          </Button>
        </div>

        {out && (
          <div className="q5-notice q5-notice--stacked">
            <p className="pk-strong q5-notice-text q5-warn">
              <span aria-hidden="true">⚠ </span>
              {t('q5.lineSoldOut', {item: name})}
              {reasonKey ? <span className="pk-muted"> · {t(reasonKey)}</span> : null}
            </p>
            <Button variant="destructive" onClick={() => onRemove(line)}>
              {t('q5.lineSoldOutAction')}
            </Button>
          </div>
        )}

        {newPrice !== null && (
          <div className="q5-notice q5-notice--stacked">
            <p className="q5-notice-text">
              {t('q5.linePriceChanged', {item: name, price: money(newPrice)})}
            </p>
            <Button variant="secondary" onClick={() => onAcceptPrice(line.line_id, newPrice)}>
              {t('q5.linePriceChangedAction')}
            </Button>
          </div>
        )}
      </Card>
    </li>
  );
}

/**
 * The table's open ticket — rounds already with the kitchen.
 *
 * It appears whenever this phone is attached to an order, which happens when the
 * guest already sent a round *or* when another phone on the same table QR did
 * (`GuestSession.active_order_id`). Without it, a second guest scanning the same
 * QR sees an empty cart and no sign of the ฿900 of food already ordered.
 *
 * The lines arrive as a prop rather than being filtered here, and the caller
 * decides whether there is a ticket at all. That same answer decides whether the
 * empty message may be said (note 6), and the two are not allowed to disagree —
 * a ticket rendering beside "there is nothing here" is the exact bug this
 * screen shipped with.
 *
 * Attribution comes from `added_by_guest_session_id`, so "yours" and "everyone
 * else's" are separable without anyone having identified themselves. When the
 * server did not attribute the lines, they are listed flat rather than guessed
 * at.
 *
 * These lines are **not** cards. A sent line is not editable, and drawing it in
 * the same white box as an editable one invites a tap that does nothing; the
 * design's receipt row — page ground, one warm hairline above it — says "this is
 * a record" without a word.
 */
function Ticket({lines}: {lines: OrderLine[]}) {
  const {t} = useI18n();
  const {session} = useGuestSession();

  const mySessionId = session?.guest_session_id ?? null;
  const attributed =
    mySessionId !== null && lines.some((line) => line.added_by_guest_session_id != null);
  const mine = lines.filter((line) => line.added_by_guest_session_id === mySessionId);
  const others = lines.filter((line) => line.added_by_guest_session_id !== mySessionId);

  return (
    <section className="pk-stack" aria-labelledby="q5-ticket">
      <div>
        <h2 className="pk-section-title" id="q5-ticket">
          {t('q7.summary')}
        </h2>
        <p className="pk-muted">{t('q7.alreadySent')}</p>
      </div>

      {attributed ? (
        <>
          {mine.length > 0 && <TicketGroup heading={t('q5.yourItems')} lines={mine} />}
          {others.length > 0 && <TicketGroup heading={t('q5.tableItems')} lines={others} />}
        </>
      ) : (
        <TicketGroup lines={lines} />
      )}

      {/*
        The server's totals block used to close the ticket here — subtotal, the
        grand total, the inclusive-VAT note and the unpaid pill. **Removed by
        owner directive, 2026-08-14:** the pinned bar already carries a figure
        (`q5.estimatedTotal`), and two money blocks on one screen is a guest
        asked to work out which of them is the one that matters.

        The money is not lost, it moved to where a bill belongs: Q14 links to
        the summary and Q17 renders the same numbers as an actual receipt, VAT
        note and all. This screen is now about *what you ordered*, and the
        `q7.alreadySent` line above says the part that matters here.

`Totals` and `TotalRow` went with it. They are not kept as dead
        code: this file's `noUnusedLocals` would reject them, and a component
        exported only to stay compilable is worse than one deleted. The
        inclusive-VAT rule they implemented (ADR-0009 — the VAT is a note under
        the total, never a row added into it) still has exactly one live
        implementation, `Ledger` in `Q17OrderConfirmation`, which is the screen
        that is actually a bill.

        One state to watch if this is ever revisited: an empty cart over a live
        ticket shows no figure at all now, because the pinned bar belongs to the
        cart and is absent there. `q7.alreadySent` and the summary link carry it
        instead.
      */}
    </section>
  );
}

function TicketGroup({heading, lines}: {heading?: string; lines: OrderLine[]}) {
  return (
    <div>
      {heading && <h3 className="pk-strong pk-muted q5-group-heading">{heading}</h3>}
      <ul className="q5-ticket-list">
        {lines.map((line) => (
          <TicketRow key={line.line_id} line={line} />
        ))}
      </ul>
    </div>
  );
}

function TicketRow({line}: {line: OrderLine}) {
  const {ln, signedMoney} = useI18n();
  const modifiers = line.modifiers ?? [];
  return (
    <li className="pk-ruled">
      <div className="q5-line-head">
        <div className="q5-line-id">
          <p className="pk-strong">
            <span className="pk-money">{line.quantity}×</span> {ln(line.name_snapshot)}
          </p>
          {modifiers.length > 0 && (
            <p className="pk-muted">
              {modifiers
                .map((modifier) => {
                  const label = ln(modifier.name_snapshot);
                  const delta =
                    modifier.price_delta_satang !== 0
                      ? ` ${signedMoney(modifier.price_delta_satang)}`
                      : '';
                  return `${label}${delta}`;
                })
                .join(' · ')}
            </p>
          )}
        </div>
        <div className="q5-line-price">
          {/* A line on an unpaid ticket. Money-shaped, no settlement meaning. */}
          <Money satang={line.line_total_satang} tone="neutral" size="md" />
        </div>
      </div>
    </li>
  );
}


/**
 * Layout only, scoped to `.q5-*`.
 *
 * Everything with a colour, a type step, a radius or a tap target is a shared
 * primitive or a `.pk-*` utility; what is left below is where the boxes go. It
 * is a `<style>` string rather than an imported `.css` file for the reason
 * `uiCss.ts` gives: vitest's `css` option defaults to false, so an imported
 * stylesheet does not exist during the tests and any geometry asserted through
 * `getComputedStyle` would silently read empty.
 *
 * No hex literals, no shadows, no size under 16px, no line-height under 1.6.
 */
const STYLES = `
.q5-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-2);
}
.q5-ticket-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.q5-group-heading {
  margin: 0 0 var(--pokpok-space-1);
}

/* Name and modifiers on the left, the line total pinned right. Top-aligned:
   the name wraps to two or three lines on a small phone and the price belongs
   beside the first of them, not floating at their centre. */
.q5-line-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--pokpok-space-4);
}
.q5-line-id {
  min-width: 0;
  align-items: flex-start;
}
.q5-line-price {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--pokpok-space-1);
  text-align: right;
  white-space: nowrap;
}
.q5-line-unit {
  font-weight: 400;
}

/* A sold-out line keeps its row and its price and gains a Palm Sugar edge. It
   is never greyed out and never dropped: the guest chose it, and a line that
   fades is a line they stop reading. */
.q5-line--out .pk-card {
  border-color: var(--color-warning);
  background: var(--color-background-muted);
}

/* Stepper on the left, the labelled delete on the right, and enough room
   between them that a thumb aiming at one cannot land on the other. Two 48px
   buttons and a readout do not fit the design's drawn row, so the row is
   redrawn — the controls are not shrunk. */
.q5-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--pokpok-space-4);
  flex-wrap: wrap;
}

/* A message and its one action. Wraps rather than squeezing the button, because
   Thai and Chinese set this sentence at very different widths. */
.q5-notice {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--pokpok-space-2);
}
.q5-notice--stacked {
  border-top: 1px solid var(--pokpok-color-border-warm);
  padding-top: var(--pokpok-space-2);
}
.q5-notice-text {
  flex: 1 1 12rem;
  min-width: 0;
}
.q5-warn {
  color: var(--color-warning);
}

.q5-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--pokpok-space-4);
  padding: var(--pokpok-space-12) 0;
  text-align: center;
}
.q5-empty .pk-btn {
  width: 100%;
}

.q5-totals {
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-2);
  padding-top: var(--pokpok-space-4);
  border-top: 1px solid var(--pokpok-color-border-warm);
}
/* The receipt tear: the one rule on this screen that is not a plain hairline,
   and it separates what the parts add up to from what the table owes. */
.q5-grand {
  border-top: 1px dashed var(--pokpok-color-border-warm);
  padding-top: var(--pokpok-space-2);
}

.q5-attrib {
  text-align: center;
}

/* The estimate and its disclaimer are one thought, so they sit tighter than the
   footer's own rhythm. The design pulls the second line up with a negative
   margin; a smaller gap says it without one. */
.q5-estimate {
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-1);
}

.q5-hold {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--pokpok-space-2);
}
`;
